import { portaalQueryOptions } from '@connected/portaal-sdk/queries/options';
import { Button } from '@connected/ui/components/button';
import { Carousel, CarouselContent, CarouselItem } from '@connected/ui/components/carousel';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@connected/ui/components/dropdown-menu';
import { Image } from '@connected/ui/components/image';
import { Separator } from '@connected/ui/components/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@connected/ui/components/table';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CheckmarkCircle } from '@/components/checkmark-circle';
import { ComingSoonModal } from '@/components/coming-soon-modal';
import { Observation } from '@/components/observation';
import {
    ScrollableTable,
    ScrollableTableBody,
    ScrollableTableCell,
    ScrollableTableHead,
    ScrollableTableHeader,
    ScrollableTableRow,
} from '@/components/scrollable-table';

export const Route = createFileRoute(
    '/_public/customer/locations/$locationId/electrical/projects/$projectId/_details/_installations/final-circuit/$circuitId/',
)({
    component: RouteComponent,
});

function RouteComponent() {
    const { t } = useTranslation('customer-module-electrical');
    const params = Route.useParams();

    const [comingSoonModalOpen, setComingSoonModalOpen] = useState<boolean>(false);

    const machinesQuery = useQuery(portaalQueryOptions.electrical.finalcircuits.view({
        ElectricalProjectFinalCircuitId: params.circuitId,
    }));

    const observationQuery = useQuery(portaalQueryOptions.electrical.observations.list({
        ElectricalProjectId: params.projectId ?? null,
        ObjectKind: 'FinalCircuit',
    }));

    return (
        <>
            <div className="flex flex-col sm:flex-row justify-between items-center">
                <div className="flex flex-col max-sm:flex-wrap gap-1 justify-start">
                    <h1 className="text-2xl font-semibold">
                        {machinesQuery?.data?.FinalCircuit?.Identifier}
                    </h1>
                    <h2 className="text-base font-medium text-text-primary/70">
                        {t('module-electrical.distribution-boards.floor')}
                        {' '}
                        {machinesQuery?.data?.FinalCircuit?.FloorNumber ?? 'Locatie onbekend'}
                        ,
                        {' '}
                        {t('module-electrical.distribution-boards.room')}
                        {' '}
                        {machinesQuery?.data?.FinalCircuit?.SpaceNumber ?? 'Locatie onbekend'}
                    </h2>
                </div>
                <div className="flex gap-3">
                    <Button
                        variant="link"
                        className="border px-3 rounded-lg h-10 w-fit text-foreground hover:no-underline hover:bg-background mt-2 md:mt-0"
                        onClick={() => setComingSoonModalOpen(true)}
                    >
                        <div className="flex items-center">
                            <p className="ml-2 mr-2 text-sm">{t('module-electrical.distribution-boards.distribution-board.buttons.notification')}</p>
                        </div>
                    </Button>
                    <DropdownMenu>
                        <DropdownMenuTrigger className="flex items-center gap-2 border px-3 rounded-lg h-10 w-1/2 text-foreground hover:no-underline hover:bg-background">
                            <span className="text-sm">Verzoek</span>
                            <ChevronDown className="w-5 h-5" />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="flex flex-col" align="end">
                            <DropdownMenuItem
                                className="p-2 focus:bg-background-secondary focus:text-popover-foreground cursor-pointer"
                                onClick={() => setComingSoonModalOpen(true)}
                            >
                                Onderhoud
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                className="p-2 focus:bg-background-secondary focus:text-popover-foreground cursor-pointer"
                                onClick={() => setComingSoonModalOpen(true)}
                            >
                                Power quality
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                className="p-2 focus:bg-background-secondary focus:text-popover-foreground cursor-pointer"
                                onClick={() => setComingSoonModalOpen(true)}
                            >
                                Offerte vervanging
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                className="p-2 focus:bg-background-secondary focus:text-popover-foreground cursor-pointer"
                                onClick={() => setComingSoonModalOpen(true)}
                            >
                                Thermografie
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                    <ComingSoonModal open={comingSoonModalOpen} onOpenChange={setComingSoonModalOpen} />
                </div>
            </div>
            <Separator className="my-5" />
            <div className="flex flex-col md:flex-row gap-4 mb-10">
                <div className="sm:w-1/4">
                    <h3 className="text-base text-text-primary/70 font-semibold">
                        <p>
                            {machinesQuery?.data?.FinalCircuit?.Identifier}
                            {' '}
                            {t('module-electrical.distribution-boards.distribution-board.images.title')}
                        </p>
                    </h3>
                </div>
                {' '}
                <div className="sm:w-3/4 rounded-2xl h-full">
                    {machinesQuery?.data?.FinalCircuit?.Files && machinesQuery?.data.FinalCircuit.Files.length > 0
                        ? (
                                <Carousel loop>
                                    <CarouselContent className="-ml-2 md:-ml-4">
                                        {machinesQuery?.data.FinalCircuit.Files.map((file, idx) => (
                                            <CarouselItem className="pl-2 md:pl-4 basis-1/1 sm:basis-1/2 lg:basis-1/3 xl:basis-1/4" key={file.ElectricalProjectFileId ?? idx}>
                                                {file.File && (
                                                    <Image
                                                        previewer
                                                        className="h-52 w-auto object-contain rounded-lg"
                                                        alt={file?.File?.Name ?? ''}
                                                        queryOptions={portaalQueryOptions.files.electricalProject({
                                                            ElectricalProjectFileId: file.ElectricalProjectFileId!,
                                                            ImageQuality: 'OriginalWebp',
                                                        })}
                                                        enabled={!!file.ElectricalProjectFileId}
                                                    />
                                                )}
                                            </CarouselItem>
                                        ))}
                                    </CarouselContent>
                                </Carousel>
                            )
                        : (
                                <div className="flex items-center justify-center h-52 text-text-primary/50 rounded-lg border-2 border-dashed">
                                    <p>{t('module-electrical.pv-installations.messages.noImagesFound')}</p>
                                </div>
                            )}
                </div>
            </div>
            <Separator className="my-5" />
            <div className="flex flex-col md:flex-row gap-4 mb-10">
                <div className="sm:w-1/4">
                    <h3 className="text-base text-text-primary/70 font-semibold">
                        {t('module-electrical.distribution-boards.distribution-board.notifications-table.title')}
                    </h3>
                </div>
                <div className="sm:w-3/4 rounded-2xl border overflow-hidden">
                    <ScrollableTable maxHeight={19.9}>
                        <ScrollableTableHeader className="bg-background-secondary">
                            <ScrollableTableRow className="h-12">
                                <ScrollableTableHead className="text-text-primary/70">
                                    {t(
                                        'module-electrical.distribution-boards.distribution-board.notifications-table.header.name',
                                    )}
                                </ScrollableTableHead>
                                <ScrollableTableHead className="text-text-primary/70 bg-background-secondary px-4">
                                    {t(
                                        'module-electrical.distribution-boards.distribution-board.notifications-table.header.status',
                                    )}
                                </ScrollableTableHead>
                                <ScrollableTableHead className="text-text-primary/70">
                                    {t(
                                        'module-electrical.distribution-boards.distribution-board.notifications-table.header.comment',
                                    )}
                                </ScrollableTableHead>
                                <ScrollableTableHead className="text-text-primary/70">
                                    {t(
                                        'module-electrical.distribution-boards.distribution-board.notifications-table.header.date',
                                    )}
                                </ScrollableTableHead>
                            </ScrollableTableRow>
                        </ScrollableTableHeader>
                        <ScrollableTableBody>
                            {observationQuery?.data?.Observations?.filter(obs => obs.ElectricalProjectFinalCircuitId === params.circuitId)?.map(observation => (
                                <ScrollableTableRow
                                    key={observation.ElectricalProjectObservationId}
                                    className="cursor-pointer hover:bg-background-secondary h-16"
                                >
                                    <ScrollableTableCell>
                                        <div className="flex items-center gap-2">
                                            <Observation
                                                color={observation?.HexColorCode ?? '#FFFFFF'}
                                                label={t(
                                                    'module-electrical.report.observations.details-table.columns.observation',
                                                )}
                                                className="scale-75"
                                                children={[]}
                                            />
                                            <Link
                                                to="/customer/locations/$locationId/electrical/projects/$projectId/final-circuit/$circuitId/observations/$observationId"
                                                params={{
                                                    locationId: params.locationId,
                                                    projectId: params.projectId,
                                                    circuitId: params.circuitId,
                                                    observationId: observation.ElectricalProjectObservationId!,
                                                }}
                                                className="font-bold text-primary cursor-pointer"
                                            >
                                                {t('module-electrical.report.observations.details-table.columns.observation')}
                                            </Link>
                                        </div>
                                    </ScrollableTableCell>

                                    <ScrollableTableCell className="px-4">
                                        <CheckmarkCircle checked={observation.IsFixed} />
                                    </ScrollableTableCell>
                                    <ScrollableTableCell>
                                        {observation.Remark}
                                    </ScrollableTableCell>
                                    <ScrollableTableCell>
                                        {observation.Date ? new Date(observation.Date).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' }) : ''}
                                    </ScrollableTableCell>
                                </ScrollableTableRow>
                            ))}
                        </ScrollableTableBody>
                        {observationQuery?.data?.Observations?.length === 0 && (
                            <div className="flex items-center justify-center h-12 text-text-primary/50 w-full">
                                <p className="w-full text-center">{t('module-electrical.pv-installations.messages.noObservationsFound')}</p>
                            </div>
                        )}
                    </ScrollableTable>
                </div>
            </div>
            <Separator className="my-5" />
            <div className="flex flex-col md:flex-row gap-4 mb-10">
                <div className="sm:w-1/4">
                    <h3 className="text-base text-text-primary/70 font-semibold">
                        {t('module-electrical.distribution-boards.distribution-board.checklist-table.title')}
                    </h3>
                    <p className="text-sm text-text-primary/70">
                        {t('module-electrical.distribution-boards.distribution-board.checklist-table.subtitle')}
                    </p>
                </div>
                <div className="w-full sm:w-3/4 rounded-2xl border ">
                    <Table className="overflow-hidden rounded-2xl">
                        <TableHeader className="bg-background-secondary">
                            <TableRow>
                                <TableHead className="text-text-primary/70">
                                    {t(
                                        'module-electrical.distribution-boards.distribution-board.checklist-table.header.part',
                                    )}
                                </TableHead>
                                <TableHead className="text-text-primary/70">
                                    {t(
                                        'module-electrical.distribution-boards.distribution-board.checklist-table.header.status',
                                    )}
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {machinesQuery?.data?.FinalCircuit?.CheckListItems?.map(item => (
                                <TableRow
                                    key={item.ElectricalProjectCheckListItemId}
                                    className="cursor-pointer hover:bg-background-secondary"
                                >
                                    <TableCell>
                                        {item.Description}
                                    </TableCell>
                                    <TableCell>
                                        {t(
                                            `module-electrical.distribution-boards.distribution-board.checklist-table.status.${item.ConclusionKind}`,
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </div>
        </>
    );
}
